import React, { useCallback, useState } from 'react';
import PanelHeading from '../../../../../components/PanelHeading';
import SegmentedControl from '../../../../../components/SegmentedControl';
import Skeleton from '../../../../../components/Skeleton';
import { TableSkeleton } from '../../../../../components/skeletons';
import { useConfirmDialog } from '../../../../../components/ConfirmDialog';
import {
  BALANCE_LEAVE_LABELS,
  BalanceLeaveType,
  FormErrors,
  LeaveAdjustment,
  LeaveEntitlement,
  LeaveSettingsDraft
} from '../../../config';
import { EmployeeLeaveState } from '../useEmployeeLeave';
import LeaveBalanceStats from './LeaveBalanceStats';
import LeaveTakenTable from './LeaveTakenTable';
import LeaveLedgerTable from './LeaveLedgerTable';
import LeaveAdjustmentForm from './LeaveAdjustmentForm';
import LeaveEntitlementSection from './LeaveEntitlementSection';
import LeaveYears from './LeaveYears';
import { formatDay, formatLeaveHours, formatSignedHours } from './leaveFormat';

interface LeaveTabProps {
  leave: EmployeeLeaveState;
  draft: LeaveSettingsDraft;
  errors: FormErrors;
  /** The modal is saving, or the viewer is read-only. */
  disabled: boolean;
  canEdit: boolean;
  onDraftChange: (patch: Partial<LeaveSettingsDraft>) => void;
}

/**
 * One employee's leave, for a manager: what is available, what was taken, what
 * was earned each pay period, and the terms it is all worked out from.
 */
const LeaveTab: React.FC<LeaveTabProps> = ({
  leave,
  draft,
  errors,
  disabled,
  canEdit,
  onDraftChange
}) => {
  const [leaveType, setLeaveType] = useState<BalanceLeaveType>('personal');
  // Years the user has opened or closed by hand. A year not in here is open
  // only if it is the current one, so the view rolls over with the calendar.
  const [toggledYears, setToggledYears] = useState<Record<number, boolean>>({});
  const [showAdjustment, setShowAdjustment] = useState<boolean>(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const { confirm, dialog } = useConfirmDialog();

  const { deleteAdjustment, deleteEntitlement } = leave;

  const handleDeleteAdjustment = useCallback(async (adjustment: LeaveAdjustment) => {
    const ok = await confirm({
      title: 'Remove adjustment',
      message: `Remove the ${formatSignedHours(Number(adjustment.hours))} adjustment "${adjustment.note}"? The balance changes by the same amount.`,
      confirmLabel: 'Remove',
      tone: 'danger'
    });
    if (!ok) return;
    setActionError(await deleteAdjustment(adjustment.id));
  }, [confirm, deleteAdjustment]);

  const handleDeleteEntitlement = useCallback(async (entitlement: LeaveEntitlement) => {
    const ok = await confirm({
      title: 'Remove entitlement',
      message: entitlement.effective_from
        ? `Remove the entitlement from ${formatDay(entitlement.effective_from)}? The one before it then runs on past that date, and the pay periods it covered are worked out again.`
        : 'Remove the entitlement from the start? That stretch goes back to the standard full-time terms, and its pay periods are worked out again.',
      confirmLabel: 'Remove',
      tone: 'danger'
    });
    if (!ok) return;
    setActionError(await deleteEntitlement(entitlement.id));
  }, [confirm, deleteEntitlement]);

  if (leave.loading) {
    return (
      <div className="employee-tab leave-tab" aria-busy="true">
        <Skeleton variant="rectangular" height={64} borderRadius={8} />
        <TableSkeleton rows={5} columns={4} />
      </div>
    );
  }

  if (leave.error || !leave.data) {
    return (
      <div className="employee-tab leave-tab">
        <div className="employee-tab__load-error" role="alert">
          <span>{leave.error ?? 'Leave could not be loaded.'}</span>
          <button type="button" className="pill-btn pill-btn--sm" onClick={() => leave.reload()}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const data = leave.data;
  const label = BALANCE_LEAVE_LABELS[leaveType];
  const isSick = leaveType === 'sick_leave';
  const years = isSick ? data.sick_years : data.annual_years;

  const openYears = new Set(
    years
      .filter((row) => toggledYears[row.year] ?? row.is_current)
      .map((row) => row.year)
  );
  const handleToggleYear = (year: number) => {
    setToggledYears((prev) => ({ ...prev, [year]: !openYears.has(year) }));
  };

  return (
    <div className="employee-tab leave-tab">
      {!data.accrual_start && (
        <p className="ui-table__empty leave-tab__notice">
          Leave is not being tracked for this person yet. It starts with their
          first timesheet week, or on the date set under Opening balance below.
        </p>
      )}

      {data.accrual_start && !data.is_active && (
        <p className="ui-table__empty leave-tab__notice">
          This person is inactive, so they are not earning leave.
        </p>
      )}

      <LeaveBalanceStats leave={data} />

      <div className="leave-tab__switch">
        <SegmentedControl<BalanceLeaveType>
          options={[
            { value: 'personal', label: BALANCE_LEAVE_LABELS.personal },
            { value: 'sick_leave', label: BALANCE_LEAVE_LABELS.sick_leave }
          ]}
          value={leaveType}
          onChange={(v) => {
            if (!v) return;
            setLeaveType(v);
            setShowAdjustment(false);
          }}
          aria-label="Leave type"
        />
        {data.accrual_start && (
          <span className="leave-tab__as-at">
            Counted from {formatDay(data.accrual_start)} · next pay period ends {formatDay(data.next_period_end)}
          </span>
        )}
      </div>

      {actionError && (
        <p className="field__error" role="alert">{actionError}</p>
      )}

      <PanelHeading
        title={`${label} by year`}
        meta={years.length > 1 ? `${years.length} years` : undefined}
        hint={isSick
          ? 'Sick leave does not carry over. Each year starts from nothing on 1 January; what a closed year had remaining is the amount to pay out. Open a year to see what made it up.'
          : 'Annual leave carries over, so each year starts on the balance the last one ended on. Open a year to see what made it up.'}
        action={canEdit && !showAdjustment ? (
          <button
            type="button"
            className="panel-heading__action"
            onClick={() => setShowAdjustment(true)}
          >
            + Adjustment
          </button>
        ) : undefined}
      />
      {showAdjustment && (
        <LeaveAdjustmentForm
          leaveType={leaveType}
          today={data.as_of}
          onSubmit={leave.addAdjustment}
          onCancel={() => setShowAdjustment(false)}
        />
      )}
      <LeaveYears
        leaveType={leaveType}
        years={years}
        hoursPerDay={data.standard_hours_per_day}
        openYears={openYears}
        onToggleYear={handleToggleYear}
        renderDetail={(row) => (
          <>
            <PanelHeading title="Taken" as="h5" meta={formatLeaveHours(Number(row.taken))} />
            <LeaveTakenTable
              leave={data}
              entries={data.entries}
              leaveType={leaveType}
              hoursPerDay={data.standard_hours_per_day}
              year={row.year}
            />
            <PanelHeading
              title="Earned"
              as="h5"
              spaced="sm"
              meta={formatLeaveHours(Number(row.accrued))}
            />
            <LeaveLedgerTable
              leave={data}
              leaveType={leaveType}
              year={row.year}
              opening={Number(row.opening)}
              canEdit={canEdit}
              onDeleteAdjustment={handleDeleteAdjustment}
            />
          </>
        )}
      />

      <LeaveEntitlementSection
        leave={data}
        draft={draft}
        errors={errors}
        disabled={disabled}
        canEdit={canEdit}
        onDraftChange={onDraftChange}
        onSaveEntitlement={leave.saveEntitlement}
        onDeleteEntitlement={handleDeleteEntitlement}
      />

      {dialog}
    </div>
  );
};

export default LeaveTab;
